import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import toastCommonProps from "../../../../common/toast";
import api from "../../../../common/api";
import { regPass } from "../../components/logic";
import { saveLoginInfo } from "../../../../features/mobile/commonSlice";
import HeadCont from "../../components/common/HeadCont";
import Btn from "../../components/common/Btn";
import InputBox from "../../components/common/InputBox";
import TitleBox from "../../components/common/TitleBox";

const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pwd, setPwd] = useState({ pass: "", again: "" });
  const [active1, setActive1] = useState("ready");
  const [active2, setActive2] = useState("ready");
  const [errorMsg, setErrorMsg] = useState({
    msg1: t('password.password_requirements', 'Password must be 8-20 characters with letters and numbers'),
    msg2: "",
  });

  const passRef = useRef(null);
  const againRef = useRef(null);

  useEffect(() => {
    // Check if token exists
    if (!token) {
      toast(
        <p>{t('password.invalid_link', 'This password reset link is invalid. Please request a new one.')}</p>,
        toastCommonProps("bottom-center", "toast_alert", 3000)
      );
      navigate('/findpass');
    }
  }, [token, navigate, t]);

  const onChangeInput1 = (e) => {
    setPwd({ ...pwd, pass: e.target.value });
    if (!regPass(e.target.value)) {
      setErrorMsg({
        msg1: t('password.password_requirements', 'Password must be 8-20 characters with letters and numbers'),
        msg2: errorMsg.msg2,
      });
      setActive1("ready");
    } else {
      setErrorMsg({ ...errorMsg, msg1: "" });
      setActive1("on");
      // Re-validate password confirmation if it has a value
      if (pwd.again) {
        if (pwd.again === e.target.value) {
          setErrorMsg({ msg1: "", msg2: t('password.password_match', 'Passwords match') });
          setActive2("on");
        } else {
          setErrorMsg({ ...errorMsg, msg2: t('password.passwords_dont_match', 'Passwords do not match') });
          setActive2("ready");
        }
      }
    }
  };

  const onChangeInput2 = (e) => {
    setPwd({ ...pwd, again: e.target.value });
    if (pwd.pass !== e.target.value) {
      setErrorMsg({
        ...errorMsg,
        msg2: t('password.confirm_password_help', 'Please enter the same password'),
      });
      setActive2("ready");
    } else {
      if (active1 === "on") {
        setErrorMsg({ ...errorMsg, msg2: t('password.password_match', 'Passwords match') });
        setActive2("on");
      } else {
        setActive2("ready");
      }
    }
  };

  const onDeleteValue = (type) => {
    if (type === "pass") {
      setPwd({ pass: "", again: "" });
      setErrorMsg({
        msg1: t('password.password_requirements', 'Password must be 8-20 characters with letters and numbers'),
        msg2: "",
      });
      passRef.current?.focus();
      setActive1("ready");
      setActive2("ready");
    } else {
      setPwd({ ...pwd, again: "" });
      setErrorMsg({ ...errorMsg, msg2: "" });
      againRef.current?.focus();
      setActive2("ready");
    }
  };

  const onSavePass = async (e) => {
    e.preventDefault();

    if (!token) {
      toast(
        <p>{t('password.invalid_link', 'This password reset link is invalid. Please request a new one.')}</p>,
        toastCommonProps("bottom-center", "toast_alert", 3000)
      );
      return;
    }

    if (active1 !== "on" || active2 !== "on") {
      return;
    }

    try {
      // Step 1: Reset password
      const resetRes = await api.post('/auth/reset-password', {
        token: token,
        newPassword: pwd.pass,
      });

      if (resetRes.data.ok) {
        const userEmail = resetRes.data.email;

        // Step 2: Automatically login with new password
        try {
          const loginRes = await api.post('/auth/email/login', {
            email: userEmail,
            password: pwd.pass,
          });

          if (loginRes.data.ok) {
            // Store JWT token
            localStorage.setItem('accessToken', loginRes.data.accessToken);

            // Save login info to Redux
            dispatch(saveLoginInfo({
              userId: loginRes.data.userId,
              nickName: loginRes.data.nickName,
              email: loginRes.data.email,
              characterPhoto: loginRes.data.characterPhoto,
              existPass: true,
            }));

            // Show success message
            toast(
              <p>{t('password.password_changed_success', 'Password changed successfully! Welcome back.')}</p>,
              toastCommonProps("bottom-center", "toast_success", 2000)
            );

            // Navigate to home
            setTimeout(() => navigate('/home'), 1500);
          } else {
            // Password reset worked but login failed - shouldn't happen but handle it
            toast(
              <p>{t('password.password_changed_login_manual', 'Password changed! Please log in with your new password.')}</p>,
              toastCommonProps("bottom-center", "toast_success", 2000)
            );
            setTimeout(() => navigate('/login'), 2000);
          }
        } catch (loginErr) {
          console.error('Auto-login error:', loginErr);
          // Password reset worked but auto-login failed
          toast(
            <p>{t('password.password_changed_login_manual', 'Password changed! Please log in with your new password.')}</p>,
            toastCommonProps("bottom-center", "toast_success", 2000)
          );
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        // Show user-friendly error message
        const errorMessage = resetRes.data.error || t('password.reset_failed', 'Failed to reset password');
        setErrorMsg({
          ...errorMsg,
          msg2: errorMessage,
        });
        toast(
          <p>{errorMessage}</p>,
          toastCommonProps("bottom-center", "toast_alert", 3000)
        );
        setPwd({ pass: "", again: "" });
        setActive1("ready");
        setActive2("ready");
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      const errorMessage = err.response?.data?.error || t('password.reset_error', 'An error occurred. Please try again.');
      toast(
        <p>{errorMessage}</p>,
        toastCommonProps("bottom-center", "toast_alert", 3000)
      );
      setPwd({ pass: "", again: "" });
      setActive1("ready");
      setActive2("ready");
    }
  };

  useEffect(() => {
    let focused = true;
    if (focused) {
      passRef.current?.focus();
    }
    return () => (focused = false);
  }, []);

  return (
    <>
      <HeadCont
        title={t('password.reset_title', 'Reset Password')}
        underline={true}
        onLeftAction={() => navigate('/login')}
      />

      <section className="mx-6 mt-[80px]">
        <TitleBox
          option={2}
          title={t('password.enter_new_password', 'Enter your new password')}
        />
        <form
          onSubmit={
            active1 === "on" && active2 === "on"
              ? onSavePass
              : (e) => e.preventDefault()
          }
          className="space-y-6 mt-6"
        >
          <InputBox
            option="pass"
            refName={passRef}
            id={"pass"}
            nameTitle={"pass"}
            placeholder={t('password.password_placeholder', 'Password')}
            onChangeAction={(e) => onChangeInput1(e)}
            value={pwd.pass || ""}
            active={active1}
            onClickAction={() => onDeleteValue("pass")}
            errorMsg={errorMsg.msg1}
          />
          <InputBox
            option="pass"
            refName={againRef}
            id={"again"}
            nameTitle={"again"}
            placeholder={t('password.confirm_password_placeholder', 'Confirm Password')}
            onChangeAction={(e) => onChangeInput2(e)}
            value={pwd.again || ""}
            active={active2}
            onClickAction={() => onDeleteValue("again")}
            errorMsg={errorMsg.msg2}
          />
          <Btn
            option={"bottom"}
            bgColor={"bg-main"}
            textColor={"text-white"}
            borderColor={"border border-main"}
            title={t('password.save_password', 'Save Password')}
            active={active1 === "on" && active2 === "on" ? "on" : "ready"}
          />
        </form>
      </section>
    </>
  );
};

export default ResetPassword;
